import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { 
  Calendar,
  Clock,
  Bell,
  TrendingUp,
  MessageSquare,
  Plus,
  Settings,
  Play,
  Pause
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { Objective } from '@/types/objectives'

interface ReviewCycle {
  id: string
  objectiveId: string
  frequency: 'weekly' | 'bi-weekly' | 'monthly' | 'quarterly'
  dayOfWeek?: number // 0-6 for weekly/bi-weekly
  dayOfMonth?: number // 1-31 for monthly
  monthOfQuarter?: number // 1-3 for quarterly
  isActive: boolean
  nextReviewDate: string
  lastReviewDate?: string
  reviewPrompts: string[]
  createdAt: string
}

interface ReviewSession {
  id: string
  cycleId: string
  objectiveId: string
  scheduledDate: string
  completedDate?: string
  status: 'pending' | 'completed' | 'skipped' | 'overdue'
  responses: {
    prompt: string
    response: string
  }[]
  progressRating: number // 1-5 scale
  blockers: string[]
  nextSteps: string[]
  notes: string
}

interface ProgressReviewCyclesProps {
  objective: Objective
  onUpdateObjective?: (objective: Partial<Objective>) => void
}

const defaultPrompts = {
  weekly: [
    "What progress have you made toward this objective this week?",
    "What obstacles or challenges did you encounter?",
    "What specific actions will you take next week?",
    "How confident are you about meeting your target timeline?"
  ],
  'bi-weekly': [
    "What measurable progress have you made in the past two weeks?",
    "Are you on track to meet your milestone dates?",
    "What resources or support do you need?",
    "What adjustments need to be made to your approach?"
  ],
  monthly: [
    "How has your objective progressed this month compared to your plan?",
    "What key insights have you gained about achieving this goal?",
    "What strategic changes are needed for next month?",
    "How does this objective align with your other priorities?"
  ],
  quarterly: [
    "What major milestones have you achieved this quarter?",
    "How has your understanding of this objective evolved?",
    "What fundamental changes are needed for the next quarter?",
    "How does this objective contribute to your annual goals?"
  ]
}

export default function ProgressReviewCycles({ objective, onUpdateObjective }: ProgressReviewCyclesProps) {
  const [cycles, setCycles] = useState<ReviewCycle[]>([])
  const [sessions, setSessions] = useState<ReviewSession[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [activeSession, setActiveSession] = useState<ReviewSession | null>(null)
  const [formData, setFormData] = useState({
    frequency: 'weekly' as ReviewCycle['frequency'],
    dayOfWeek: 1,
    dayOfMonth: 1,
    monthOfQuarter: 1,
    customPrompts: [] as string[]
  })

  // Load existing cycles and sessions
  useEffect(() => {
    loadReviewData()
  }, [objective.id])

  // Check for overdue sessions
  useEffect(() => {
    const interval = setInterval(checkForOverdueSessions, 60000) // Check every minute
    return () => clearInterval(interval)
  }, [sessions])

  const loadReviewData = () => {
    // In a real app, this would load from API
    const storedCycles = localStorage.getItem(`review-cycles-${objective.id}`)
    const storedSessions = localStorage.getItem(`review-sessions-${objective.id}`)
    
    if (storedCycles) {
      setCycles(JSON.parse(storedCycles))
    }
    if (storedSessions) {
      setSessions(JSON.parse(storedSessions))
    }
  }

  const saveReviewData = (newCycles: ReviewCycle[], newSessions: ReviewSession[]) => {
    localStorage.setItem(`review-cycles-${objective.id}`, JSON.stringify(newCycles))
    localStorage.setItem(`review-sessions-${objective.id}`, JSON.stringify(newSessions))
  }

  const calculateNextReviewDate = (frequency: ReviewCycle['frequency'], dayOfWeek?: number, dayOfMonth?: number): string => {
    const now = new Date()
    const nextDate = new Date()

    switch (frequency) {
      case 'weekly': {
        const daysUntilNext = ((dayOfWeek || 1) - now.getDay() + 7) % 7
        nextDate.setDate(now.getDate() + (daysUntilNext === 0 ? 7 : daysUntilNext))
        break
      }
      case 'bi-weekly': {
        const daysUntilBiWeekly = ((dayOfWeek || 1) - now.getDay() + 14) % 14
        nextDate.setDate(now.getDate() + (daysUntilBiWeekly === 0 ? 14 : daysUntilBiWeekly))
        break
      }
      case 'monthly':
        nextDate.setMonth(now.getMonth() + 1)
        nextDate.setDate(dayOfMonth || 1)
        if (nextDate < now) {
          nextDate.setMonth(nextDate.getMonth() + 1)
        }
        break
      case 'quarterly': {
        const currentQuarter = Math.floor(now.getMonth() / 3)
        const nextQuarter = (currentQuarter + 1) % 4
        nextDate.setMonth(nextQuarter * 3)
        nextDate.setDate(1)
        if (nextDate < now) {
          nextDate.setFullYear(nextDate.getFullYear() + 1)
        }
        break
      }
    }

    return nextDate.toISOString()
  }

  const createReviewCycle = () => {
    const newCycle: ReviewCycle = {
      id: `cycle-${Date.now()}`,
      objectiveId: objective.id,
      frequency: formData.frequency,
      dayOfWeek: formData.frequency === 'weekly' || formData.frequency === 'bi-weekly' ? formData.dayOfWeek : undefined,
      dayOfMonth: formData.frequency === 'monthly' ? formData.dayOfMonth : undefined,
      monthOfQuarter: formData.frequency === 'quarterly' ? formData.monthOfQuarter : undefined,
      isActive: true,
      nextReviewDate: calculateNextReviewDate(formData.frequency, formData.dayOfWeek, formData.dayOfMonth),
      reviewPrompts: formData.customPrompts.length > 0 ? formData.customPrompts : defaultPrompts[formData.frequency],
      createdAt: new Date().toISOString()
    }

    const updatedCycles = [...cycles, newCycle]
    setCycles(updatedCycles)
    saveReviewData(updatedCycles, sessions)
    setShowCreateForm(false)
    setFormData({
      frequency: 'weekly',
      dayOfWeek: 1,
      dayOfMonth: 1,
      monthOfQuarter: 1,
      customPrompts: []
    })
  }

  const checkForOverdueSessions = () => {
    const now = new Date()
    const updatedSessions = sessions.map(session => {
      if (session.status === 'pending' && new Date(session.scheduledDate) < now) {
        return { ...session, status: 'overdue' as const }
      }
      return session
    })

    if (JSON.stringify(updatedSessions) !== JSON.stringify(sessions)) {
      setSessions(updatedSessions)
      saveReviewData(cycles, updatedSessions)
    }
  }

  const startReviewSession = (cycle: ReviewCycle) => {
    const newSession: ReviewSession = {
      id: `session-${Date.now()}`,
      cycleId: cycle.id,
      objectiveId: objective.id,
      scheduledDate: cycle.nextReviewDate,
      status: 'pending',
      responses: cycle.reviewPrompts.map(prompt => ({ prompt, response: '' })),
      progressRating: 3,
      blockers: [],
      nextSteps: [],
      notes: ''
    }

    setActiveSession(newSession)
  }

  const completeReviewSession = (session: ReviewSession) => {
    const completedSession = {
      ...session,
      completedDate: new Date().toISOString(),
      status: 'completed' as const
    }

    const updatedSessions = [...sessions.filter(s => s.id !== session.id), completedSession]
    setSessions(updatedSessions)

    // Update the cycle's next review date
    const updatedCycles = cycles.map(cycle => {
      if (cycle.id === session.cycleId) {
        return {
          ...cycle,
          lastReviewDate: completedSession.completedDate,
          nextReviewDate: calculateNextReviewDate(cycle.frequency, cycle.dayOfWeek, cycle.dayOfMonth)
        }
      }
      return cycle
    })
    setCycles(updatedCycles)

    saveReviewData(updatedCycles, updatedSessions)
    setActiveSession(null)

    // Trigger objective update if needed
    if (onUpdateObjective && session.progressRating <= 2) {
      onUpdateObjective({
        status: 'on-hold' // Flag objectives with poor progress ratings
      })
    }
  }

  const toggleCycleStatus = (cycleId: string) => {
    const updatedCycles = cycles.map(cycle => 
      cycle.id === cycleId ? { ...cycle, isActive: !cycle.isActive } : cycle
    )
    setCycles(updatedCycles)
    saveReviewData(updatedCycles, sessions)
  }

  const upcomingSessions = cycles
    .filter(cycle => cycle.isActive)
    .map(cycle => ({
      cycle,
      daysUntil: Math.ceil((new Date(cycle.nextReviewDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    }))
    .sort((a, b) => a.daysUntil - b.daysUntil)

  const recentSessions = sessions
    .filter(session => session.status === 'completed')
    .sort((a, b) => new Date(b.completedDate!).getTime() - new Date(a.completedDate!).getTime())
    .slice(0, 3)

  if (activeSession) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Progress Check-in
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {activeSession.responses.map((response, index) => (
              <div key={index} className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {response.prompt}
                </label>
                <Textarea
                  value={response.response}
                  onChange={(e) => setActiveSession({
                    ...activeSession,
                    responses: activeSession.responses.map((r, i) => 
                      i === index ? { ...r, response: e.target.value } : r
                    )
                  })}
                  placeholder="Your response..."
                  className="min-h-[80px]"
                />
              </div>
            ))}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Progress Impact & Growth Indicator (1-5)
                </label>
                <Select 
                  value={activeSession.progressRating.toString()} 
                  onValueChange={(value) => setActiveSession({
                    ...activeSession,
                    progressRating: parseInt(value)
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Far behind</SelectItem>
                    <SelectItem value="2">2 - Behind schedule</SelectItem>
                    <SelectItem value="3">3 - On track</SelectItem>
                    <SelectItem value="4">4 - Ahead of schedule</SelectItem>
                    <SelectItem value="5">5 - Exceeding expectations</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Additional Notes
              </label>
              <Textarea
                value={activeSession.notes}
                onChange={(e) => setActiveSession({
                  ...activeSession,
                  notes: e.target.value
                })}
                placeholder="Any additional thoughts or observations..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setActiveSession(null)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => completeReviewSession(activeSession)}
                disabled={!activeSession.responses.every(r => r.response.trim())}
              >
                Complete Check-In
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Progress Check-in Cycles</h3>
          <p className="text-sm text-gray-600">Automated check-ins to track objective progress</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Check-in Cycle
        </Button>
      </div>

      {/* Upcoming Reviews */}
      {upcomingSessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-600" />
              Upcoming Check-ins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingSessions.map(({ cycle, daysUntil }) => (
                <div key={cycle.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      daysUntil <= 1 ? 'bg-red-100' : daysUntil <= 3 ? 'bg-yellow-100' : 'bg-blue-100'
                    }`}>
                      <Calendar className={`h-4 w-4 ${
                        daysUntil <= 1 ? 'text-red-600' : daysUntil <= 3 ? 'text-yellow-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{cycle.frequency} Check-in</p>
                      <p className="text-sm text-gray-600">
                        {daysUntil === 0 ? 'Due today' : 
                         daysUntil === 1 ? 'Due tomorrow' : 
                         `Due in ${daysUntil} days`}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => startReviewSession(cycle)}
                    className="gap-2"
                  >
                    <Play className="h-4 w-4" />
                    Start Check-in
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Cycles */}
      {cycles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-gray-600" />
              Check-in Cycles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cycles.map((cycle) => (
                <div key={cycle.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${cycle.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Clock className={`h-4 w-4 ${cycle.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{cycle.frequency} Check-ins</p>
                      <p className="text-sm text-gray-600">
                        Next: {new Date(cycle.nextReviewDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={cycle.isActive ? "default" : "secondary"}>
                      {cycle.isActive ? 'Active' : 'Paused'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCycleStatus(cycle.id)}
                    >
                      {cycle.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Reviews */}
      {recentSessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Recent Check-ins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentSessions.map((session) => (
                <div key={session.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">
                      {new Date(session.completedDate!).toLocaleDateString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        session.progressRating >= 4 ? "default" : 
                        session.progressRating >= 3 ? "secondary" : 
                        "destructive"
                      }>
                        Impact & Growth Indicator: {session.progressRating}/5
                      </Badge>
                    </div>
                  </div>
                  {session.notes && (
                    <p className="text-sm text-gray-600">{session.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle>Create Check-in Cycle</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Check-in Frequency
                  </label>
                  <Select 
                    value={formData.frequency} 
                    onValueChange={(value: ReviewCycle['frequency']) => setFormData(prev => ({ ...prev, frequency: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(formData.frequency === 'weekly' || formData.frequency === 'bi-weekly') && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Day of Week
                    </label>
                    <Select 
                      value={formData.dayOfWeek.toString()} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, dayOfWeek: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.frequency === 'monthly' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Day of Month
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.dayOfMonth}
                      onChange={(e) => setFormData(prev => ({ ...prev, dayOfMonth: parseInt(e.target.value) }))}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={createReviewCycle}>
                    Create Cycle
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
